<template>
    <BlokContainer
        v-editable="blok"
        size="default"
        :anchor-id="blok.anchor_id"
        :data-map-uid="blok._uid"
        background-color="white"
    >
        <div v-if="blok.heading" class="flex flex-row">
            <div class="basis-full">
                <BaseHeading :level="3" as="h3" class="mb-[16px]">
                    {{ blok.heading }}
                </BaseHeading>
            </div>
        </div>
        <div
            class="tabs-wrapper flex flex-row overflow-hidden overflow-x-scroll"
        >
            <div class="basis-full">
                <ul class="tabs mb-[24px] flex gap-[16px]">
                    <li
                        v-for="(item, index) in siteConfig.content.contact_info"
                        :key="item._uid"
                        :class="{
                            'tab-item inline-flex px-[8px] text-center transition after:transition': true,
                            'tab-active': index == 0,
                        }"
                    >
                        <DefaultLink
                            url="#"
                            class="!text-grey-500 pt-[20px] pb-[20px] whitespace-nowrap focus-visible:outline-none"
                            :title="item.tab_name"
                            :hover-effect="false"
                            @click="
                                (event: MouseEvent) =>
                                    moveMap(
                                        event,
                                        blok._uid,
                                        `${item.latitude}`,
                                        `${item.longitude}`,
                                        `${item.tab_name}`
                                    )
                            "
                        ></DefaultLink>
                    </li>
                </ul>
            </div>
        </div>
        <div class="flex flex-col gap-[24px] lg:flex-row">
            <div class="basis-full lg:basis-8/12">
                <GoogleMap
                    v-if="
                        siteConfig.content.contact_info &&
                        siteConfig.content.contact_info.length
                    "
                    :id="`map-${blok._uid}`"
                    ref="mapRef"
                    :map-id="`map-${blok._uid}`"
                    :disable-default-ui="true"
                    :zoom-control="blok.map_zoom_controls"
                    :disable-double-click-zoom="!blok.map_double_click_zoom"
                    :draggable="blok.map_draggable"
                    class="map w-full overflow-hidden rounded-2xl lg:h-full lg:min-h-[400px]"
                    :api-key="siteConfig.content.google_maps_api_key"
                    :data-lat="
                        Number(siteConfig.content.contact_info?.[0]?.latitude)
                    "
                    :data-lng="
                        Number(siteConfig.content.contact_info?.[0]?.longitude)
                    "
                    :center="{
                        lat: Number(
                            siteConfig.content.contact_info?.[0]?.latitude
                        ),
                        lng: Number(
                            siteConfig.content.contact_info?.[0]?.longitude
                        ),
                    }"
                    :zoom="Number(blok.map_zoom_level)"
                ></GoogleMap>
            </div>
            <div
                class="bg-grey-50 basis-full rounded-2xl px-[24px] pt-[24px] pb-[24px] md:px-[32px] md:pt-[32px] md:pb-[32px] lg:basis-4/12 xl:pt-[40px]"
            >
                <div
                    v-for="(contact_info, index) in siteConfig.content
                        .contact_info"
                    :key="contact_info._uid"
                    :data-tab="contact_info.tab_name"
                    :class="{
                        hidden: index !== 0,
                    }"
                >
                    <BaseHeading
                        data-field="title"
                        :level="4"
                        as="span"
                        :class="{
                            'mb-[16px]': true,
                        }"
                    >
                        {{ contact_info.title }}
                    </BaseHeading>

                    <div
                        class="flex flex-col gap-x-[16px] md:flex-row lg:flex-col"
                    >
                        <div
                            v-if="contact_info.column_1"
                            class="section-postal-address basis-full"
                        >
                            <RichText
                                data-field="column_1"
                                :rich-text="contact_info.column_1"
                            />
                        </div>
                        <div
                            v-if="contact_info.column_2"
                            class="section-visiting-address mt-[16px] basis-full md:mt-0 lg:mt-[16px]"
                        >
                            <RichText
                                data-field="column_2"
                                :rich-text="contact_info.column_2"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </BlokContainer>
</template>

<script setup lang="ts">
import BlokContainer from '@/components/containers/BlokContainer.vue';
import BaseHeading from '@/components/ui/BaseHeading.vue';
import DefaultLink from '@/components/ui/DefaultLink.vue';
import RichText from '@/components/ui/RichText.vue';
import { useNuxtApp } from 'nuxt/app';
import { defineComponent, ref, watch } from 'vue';
import { GoogleMap } from 'vue3-google-map';
import type { ISbStoryData } from '@storyblok/js';
import type {
    MapAndContactInfoStoryblok,
    SiteConfigStoryblok,
} from '@/types/storyblok-types';
const siteConfig = useNuxtApp()
    .$siteConfig as ISbStoryData<SiteConfigStoryblok>;
defineProps<{ blok: MapAndContactInfoStoryblok }>();
</script>
<script lang="ts">
const mapRef = ref<typeof GoogleMap | null>(null);
let markers: google.maps.marker.AdvancedMarkerElement[] = [];
watch(
    () => mapRef.value?.ready,
    (ready) => {
        if (!ready) return;

        // When map is loaded and ready
        const map_div = mapRef.value?.map.getDiv();
        const map_el = map_div.parentElement;
        const map = mapRef.value?.map;

        // Add the map marker for the main tab
        const marker = new google.maps.marker.AdvancedMarkerElement({
            position: {
                lat: parseFloat(map_el.dataset.lat),
                lng: parseFloat(map_el.dataset.lng),
            },
            map: map,
        });
        markers.push(marker);
    }
);

export default defineComponent({
    methods: {
        moveMap(
            event: MouseEvent,
            uid: string,
            lat: string,
            lng: string,
            tab_name: string
        ) {
            event.preventDefault();
            const parentElement = document.querySelector(
                `[data-map-uid="${uid}"]`
            );
            //const map_div = mapRef.value?.map.getDiv();
            const clicked_el = event.target;
            const li_el = (clicked_el as HTMLElement).closest('li');
            const parent_el = (clicked_el as HTMLElement).closest('ul');
            const li_elements = parent_el?.querySelectorAll('li');
            const map = mapRef.value?.map;
            const latLng = {
                lat: parseFloat(lat),
                lng: parseFloat(lng),
            };

            // Remove 'active' from all tabs
            li_elements?.forEach(function (el: any, i: any) {
                el.classList.remove('tab-active');
            });

            // Set 'tab-active' on the tab that was selected
            li_el?.classList.add('tab-active');

            // Move the map
            mapRef.value?.map.panTo(latLng);

            // Populate all the text fields with data
            if (parentElement) {
                parentElement.querySelectorAll('[data-tab]').forEach((el) => {
                    el.classList.add('hidden');
                });

                parentElement
                    .querySelectorAll('[data-tab="' + tab_name + '"]')
                    .forEach((el) => {
                        el.classList.remove('hidden');
                    });
            }

            // Remove all existing markers from the map
            markers.forEach(function (marker) {
                if (marker.map) {
                    marker.map = null;
                }
            });
            markers = [];

            // Create new marker & push it to the markers array
            const marker = new google.maps.marker.AdvancedMarkerElement({
                position: latLng,
                map: map,
            });
            markers.push(marker);
        },
    },
});
</script>
<style scoped lang="scss">
@reference "../../assets/css/main.css";
.map {
    // MD and down
    @media (max-width: 1023px) {
        aspect-ratio: 3/2;
    }

    // vue3-google-map's SSR (mjs) and browser (umd) builds use different
    // scope ids, so its own `.mapdiv { height: 100% }` never matches after
    // hydration and the map collapses to 0px.
    :deep(.mapdiv) {
        height: 100%;
    }
}

.tabs-wrapper {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    .tabs {
        border-bottom: 1px solid rgba(217, 214, 214, 1);

        .tab-item:has(:focus-visible) {
            outline: 2px solid #000000;
            outline-offset: -2px;
        }

        .tab-active {
            position: relative;

            a {
                color: var(--color-base-black) !important;
            }

            &:after {
                position: absolute;
                width: 100%;
                content: '';
                bottom: 0;
                height: 4px;
                left: 0;
                background: rgba(0, 0, 0, 1);
                border-top-left-radius: 4px;
                border-top-right-radius: 4px;
            }
        }
    }
}
</style>
